import React, { useState } from "react";
import StateCitySelect from "../../../StateCitySelect";
import { resolveStateCode } from "../../../../utils/stateUtils";

const TRANSFER_TYPES = ["Brokerage or Trust Company", "Mutual Fund Company Transfer", "Bank or Credit Union Transfer", "Annuity Transfer", "Transfer Agent Transfer", "Broker Dealer Change Form"];

const MAIN_FIELDS = [
  { key: "old_account_number", label: "Existing Account Number", type: "text" },
  { key: "delivering_firm_name", label: "Delivering Firm Name", type: "text" },
  { key: "is_full_transfer", label: "Full Transfer", type: "toggle" },
  { key: "transfer_type", label: "Transfer Type", type: "select", options: TRANSFER_TYPES },
];

const ADDR_FIELDS = [
  { key: "address_line_1", label: "Address Line 1" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "postal_code", label: "Postal Code" },
  { key: "country", label: "Country" },
  { key: "phone", label: "Phone" },
];

const isMissing = (val) => val === null || val === undefined || val === "";

const formatPhoneDisplay = (value) => {
  const digits = (value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const TransferInfoForm = ({ householdData, fieldData, missingFields, entityId, onSave, onSaveAndExit, saving, nextLabel }) => {
  const account = fieldData || {};
  const existing = account.transfer_info || {};
  const existingAddr = existing.address || {};

  const missingMain = MAIN_FIELDS.filter((f) => isMissing(existing[f.key]));
  const missingAddr = ADDR_FIELDS.filter((f) => isMissing(existingAddr[f.key]));
  const hasMissingLocation = missingAddr.some((f) =>
    ["city", "state", "country"].includes(f.key),
  );
  const missingAddressLineFields = missingAddr.filter(
    (f) => f.key === "address_line_1",
  );
  const missingBottomFields = missingAddr.filter(
    (f) => f.key === "postal_code" || f.key === "phone",
  );
  const missingCount = missingMain.length + missingAddr.length;

  const [formData, setFormData] = useState(() => {
    const initial = {};
    missingMain.forEach((f) => {
      if (f.key === "is_full_transfer") initial[f.key] = true;
      else if (f.key === "transfer_type") initial[f.key] = "Brokerage or Trust Company";
      else initial[f.key] = "";
    });
    missingAddr.forEach((f) => {
      initial[f.key] = f.key === "country" ? "USA" : "";
    });
    return initial;
  });

  const handleChange = (field, value) => {
    if (field === "phone") {
      setFormData((prev) => ({ ...prev, [field]: formatPhoneDisplay(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const buildPayload = () => {
    const transfer_info = { ...existing };
    missingMain.forEach((f) => {
      if (f.key === "is_full_transfer") {
        transfer_info.is_full_transfer = formData.is_full_transfer;
      } else {
        const val = formData[f.key]?.trim?.() ?? formData[f.key];
        if (val) transfer_info[f.key] = val;
      }
    });
    if (missingAddr.length > 0) {
      const addr = { ...(existing.address || {}) };
      missingAddr.forEach((f) => {
        const raw = formData[f.key]?.trim() || null;
        if (f.key === "state" && raw) {
          addr[f.key] = resolveStateCode(raw);
        } else {
          addr[f.key] = f.key === "phone" && raw ? raw.replace(/\D/g, "") : raw;
        }
      });
      transfer_info.address = addr;
    }
    return { accounts: { [entityId]: { transfer_info } } };
  };

  const handleSubmit = (cb) => cb(buildPayload());

  if (missingCount === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">All transfer details have been filled.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        <span className="text-sm text-red-500 font-medium">
          ● {missingCount} Required field{missingCount !== 1 ? "s" : ""} missing
        </span>
      </div>

      {missingMain.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
          {missingMain.map((f) => (
            <div key={f.key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {f.label} <span className="text-red-500">*</span>
              </label>
              {f.type === "toggle" ? (
                <div className="flex gap-2">
                  <button type="button" onClick={() => handleChange(f.key, true)} className={`px-4 py-1.5 rounded-lg text-sm font-medium ${formData[f.key] ? "bg-emerald-500 text-white" : "bg-white border border-gray-300 text-gray-600"}`}>Yes</button>
                  <button type="button" onClick={() => handleChange(f.key, false)} className={`px-4 py-1.5 rounded-lg text-sm font-medium ${!formData[f.key] ? "bg-red-500 text-white" : "bg-white border border-gray-300 text-gray-600"}`}>No</button>
                </div>
              ) : f.type === "select" ? (
                <select value={formData[f.key]} onChange={(e) => handleChange(f.key, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
                  {f.options.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              ) : (
                <input type="text" value={formData[f.key]} onChange={(e) => handleChange(f.key, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
              )}
            </div>
          ))}
        </div>
      )}

      {missingAddr.length > 0 && (
        <>
          <h4 className="text-sm font-semibold text-gray-700 mt-6 mb-4">Transfer Address</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
            {missingAddressLineFields.map((f) => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {f.label} <span className="text-red-500">*</span>
                </label>
                <input type="text" value={formData[f.key]} onChange={(e) => handleChange(f.key, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
              </div>
            ))}
            {hasMissingLocation && (
              <div className="md:col-span-2 bg-emerald-50/50 p-4 rounded-xl border border-emerald-100">
                <StateCitySelect
                  countryValue={
                    formData.country !== undefined
                      ? formData.country
                      : existingAddr.country || "USA"
                  }
                  stateValue={
                    formData.state !== undefined
                      ? formData.state
                      : existingAddr.state || ""
                  }
                  cityValue={
                    formData.city !== undefined
                      ? formData.city
                      : existingAddr.city || ""
                  }
                  onCountryChange={(val) => handleChange("country", val)}
                  onStateChange={(val) => handleChange("state", val)}
                  onCityChange={(val) => handleChange("city", val)}
                  isEditing={true}
                  stateLabel="State"
                  cityLabel="City"
                />
              </div>
            )}
            {missingBottomFields.map((f) => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {f.label} {f.key !== "phone" && <span className="text-red-500">*</span>}
                </label>
                <input type={f.key === "phone" ? "tel" : "text"} value={formData[f.key]} onChange={(e) => handleChange(f.key, e.target.value)} placeholder={f.key === "phone" ? "(XXX) XXX-XXXX" : ""} maxLength={f.key === "phone" ? 14 : f.key === "postal_code" ? 5 : undefined} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
              </div>
            ))}
          </div>
        </>
      )}

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button onClick={() => handleSubmit(onSaveAndExit)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">Save & Exit</button>
        <button onClick={() => handleSubmit(onSave)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2">
          {saving && <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

export default TransferInfoForm;
